/**
 * Case studies (Resources › Case studies). All anonymous by decision — never name a client,
 * and no invented quotes: a case study speaks through its facts, not testimonials.
 *
 * Standard structure, top to bottom: at a glance → the client → the challenge → goals →
 * how we delivered (phases) → the solution on TurfAI → how it runs day to day →
 * results (before/after + highlights) → what's next.
 */

export interface CaseStudy {
  slug: string;
  /** page id used by onNavigate */
  pageId: string;
  sector: string;
  /** card title */
  title: string;
  headline: string;
  standfirst: string;
  glance: { client: string; scale: string; region: string; deployment: string; timeline: string };
  context: string;
  challenge: { summary: string; points: string[] };
  goals: string[];
  approach: { phase: string; duration: string; body: string }[];
  solution: { title: string; body: string }[];
  howItRuns: string[];
  snapshot: { label: string; before: string; after: string }[];
  capabilities: string[];
  highlights: { metric: string; label: string }[];
  result: string;
  next: string;
  related: { page: string; label: string };
  metaDescription: string;
}

const cs = (c: Omit<CaseStudy, "pageId">): CaseStudy => ({ ...c, pageId: `case-${c.slug}` });

export const caseStudies: CaseStudy[] = [
  cs({
    slug: "healthcare-home-health",
    sector: "Healthcare",
    title: "Chart audit and intake for a home-health provider",
    headline: "How a home-health and hospice provider audits charts at 11× the pace — with clinicians still in control.",
    standfirst:
      "Charts, admissions and visit notes were piling up faster than clinicians could review them. TurfAI now drafts, checks and routes the paperwork; clinicians confirm every field.",
    glance: {
      client: "Post-acute home health and hospice provider",
      scale: "Thousands of charts a month across home health, hospice and therapy",
      region: "United States",
      deployment: "Private cloud, Data Shield on every model call",
      timeline: "Pilot in 6 weeks, production in 12",
    },
    context:
      "The provider runs home health, hospice and therapy services, where every visit produces documentation that must be audited against payer and regulatory rules before it can be billed. Clinical quality teams were spending most of their week reading charts rather than improving care.",
    challenge: {
      summary: "Documentation volume grew faster than the clinical team, and errors were caught weeks after the visit — when they were expensive to fix.",
      points: [
        "Chart audits done by hand against chart-type rules, with errors found weeks too late.",
        "Admissions held up by missing items — prior authorisation not started, ordering physician ID missing.",
        "Visit notes rewritten to fit the assessment form clinicians must file.",
        "Any automation had to keep clinicians accountable and patient data out of model logs.",
      ],
    },
    goals: [
      "Audit every chart, not a sample, without adding headcount.",
      "Catch blocking gaps at admission, not at billing.",
      "Make every AI suggestion traceable to the exact line in the source document.",
    ],
    approach: [
      { phase: "Discovery", duration: "2 weeks", body: "Mapped chart types, audit rules and the fields clinicians sign off, and agreed the confidence threshold below which a clinician must review." },
      { phase: "Pilot", duration: "4 weeks", body: "Ran chart audit on one service line in parallel with the human audit, comparing every decision and tuning prompts in Prompt Lab." },
      { phase: "Production", duration: "6 weeks", body: "Rolled out chart audit across service lines, then added the intake and visit-note workflows with clinician sign-off built in." },
    ],
    solution: [
      { title: "Chart audit agents", body: "Every chart reviewed against its rules, returning a decision, a confidence score and the reasoning; anything below threshold goes to a clinician." },
      { title: "Intake workflow", body: "A hospital discharge summary becomes an admission record, and sign-off stops on blocking gaps such as missing prior authorisation." },
      { title: "Documentation workflow", body: "A dictated visit note is mapped to the assessment form, field by field, with the source quote behind each." },
      { title: "Data Shield", body: "Patient identifiers are replaced with tokens before any model sees the text, and restored only inside the provider's boundary." },
    ],
    howItRuns: [
      "A chart, discharge summary or visit note arrives from the EHR.",
      "Data Shield tokenises patient identifiers.",
      "Agents audit, extract or map the document, attaching a confidence score and source quote to every field.",
      "Low-confidence fields and blocking gaps go to a named clinician.",
      "The clinician accepts, edits or rejects; the decision and its evidence land in the audit trail.",
    ],
    snapshot: [
      { label: "Charts audited", before: "A sample", after: "Every chart" },
      { label: "Audit throughput", before: "1×", after: "11×" },
      { label: "Errors found", before: "Weeks later", after: "Same day" },
      { label: "Admission record", before: "Hours", after: "Minutes" },
    ],
    capabilities: ["AI Agents", "Data Shield", "Human-in-the-loop", "Prompt Lab", "Audit trail"],
    highlights: [
      { metric: "11×", label: "clinical throughput" },
      { metric: "99.4%", label: "chart-audit accuracy" },
      { metric: "0", label: "headcount added" },
      { metric: "100%", label: "of charts audited, not sampled" },
    ],
    result: "11× clinical throughput at 99.4% chart-audit accuracy, with no headcount added — and admissions drafted in minutes instead of hours.",
    next: "Prior authorisation checks against each payer's requirements, using the same agents and sign-off pattern.",
    related: { page: "solutions-healthcare", label: "Healthcare on TurfAI" },
    metaDescription: "A home-health and hospice provider audits charts at 11× the pace at 99.4% accuracy on TurfAI, with clinicians confirming every field.",
  }),
  cs({
    slug: "bfsi-document-intelligence",
    sector: "BFSI",
    title: "Document intelligence from days to minutes",
    headline: "How a BFSI platform turned unstructured documents into credit decisions in minutes.",
    standfirst:
      "A financial-services product company wanted its clients to decide on loans and credit in minutes, not days — without sending a single document outside the bank's walls.",
    glance: {
      client: "Document-intelligence product company serving banks and lenders",
      scale: "Loan files, statements and KYC packs for multiple enterprise clients",
      region: "India and the Middle East",
      deployment: "Private cloud or on-premise, per client",
      timeline: "First release in 6 months; platform built over 12",
    },
    context:
      "The client sells a document-intelligence platform to banks, lenders and insurers. Its customers needed credit and onboarding decisions that took days of manual reading to happen in minutes — and they would only buy if no document ever left their own infrastructure.",
    challenge: {
      summary: "Every bank's documents look different, every bank's security review is strict, and the platform had to stay accurate as volumes grew tenfold.",
      points: [
        "Loan files, statements and KYC packs arrived as unstructured documents in every format.",
        "Bank-grade security, with private-cloud and on-premise deployment, was non-negotiable.",
        "Model providers change fast; locking the product to one would be a commercial risk.",
        "The platform had to stay accurate as volumes grew tenfold.",
      ],
    },
    goals: [
      "Cut complex credit decisions from days to minutes.",
      "Pass bank security reviews with on-premise deployment.",
      "Swap model providers without rebuilding the product.",
    ],
    approach: [
      { phase: "Foundation", duration: "3 months", body: "Stood up the document pipeline, the model abstraction layer and the security model for on-premise deployment." },
      { phase: "First release", duration: "3 months", body: "Shipped extraction and classification for loan files and KYC packs with per-field confidence and a review queue." },
      { phase: "Credit intelligence", duration: "6 months", body: "Added automated credit assessment and memo drafting, scaled to enterprise volumes, and hardened for multiple clients." },
    ],
    solution: [
      { title: "Document intelligence workflows", body: "Every document classified, extracted and validated, with confidence per field and a review queue for the rest." },
      { title: "Automated credit assessment", body: "Agents assemble the facts, score the risk and draft the memo for a credit officer to approve." },
      { title: "Model choice without lock-in", body: "TurfAI's multi-model layer lets each step use the provider that fits — or a self-hosted model on site." },
      { title: "Inside the perimeter", body: "Deployed in each client's own environment, with Data Shield and a full audit trail on every decision." },
    ],
    howItRuns: [
      "A loan file or KYC pack is uploaded or arrives by API.",
      "Documents are classified and fields extracted with a confidence score each.",
      "Low-confidence fields go to a reviewer; the rest flow straight through.",
      "Agents assemble the financial picture and draft a credit memo.",
      "A credit officer approves, and the decision is written back with its full trail.",
    ],
    snapshot: [
      { label: "Credit decision", before: "Days", after: "Minutes" },
      { label: "Documents read by hand", before: "All of them", after: "Exceptions only" },
      { label: "Deployment", before: "Cloud only", after: "Cloud or on-prem" },
      { label: "Model providers", before: "One, hard-wired", after: "Any, per step" },
    ],
    capabilities: ["Document extraction", "AI Agents", "Multi-model", "Data Shield", "On-prem deployment"],
    highlights: [
      { metric: "Minutes", label: "for complex credit decisions, down from days" },
      { metric: "24×", label: "employee productivity at a lender" },
      { metric: "On-prem", label: "bank-grade deployment" },
      { metric: "10×", label: "volume headroom designed in" },
    ],
    result: "Complex credit decisions cut from days to minutes, and 24× employee productivity at a lender running the same rails.",
    next: "Trade-finance documents — bills of lading, packing lists and invoices — merged into a single docket.",
    related: { page: "solutions-bfsi", label: "BFSI on TurfAI" },
    metaDescription: "A BFSI platform cut complex credit decisions from days to minutes with document intelligence and automated credit assessment on TurfAI.",
  }),
  cs({
    slug: "hr-conversational-ai",
    sector: "HR Tech",
    title: "A conversational HR assistant in eight weeks",
    headline: "How a global HR services provider put an AI assistant in front of employees in 34 countries — in eight weeks.",
    standfirst:
      "A human capital management provider handling more than a million transactions a month was buried in repetitive policy, leave and benefits questions. It needed a working assistant, on-premise, fast enough to win budget.",
    glance: {
      client: "Global human capital management (HCM) provider",
      scale: "1M+ transactions a month, employees in 34 countries",
      region: "Global",
      deployment: "On-premise in the provider's data centres",
      timeline: "Live in eight weeks",
    },
    context:
      "The provider runs payroll, compliance and workforce services for enterprises in 34 countries. Its support teams answered the same policy, leave and benefits questions all day, for every client, in every region.",
    challenge: {
      summary: "Repetitive questions were swamping support, data-privacy rules ruled out a public cloud assistant, and the team needed proof — not a slide deck — to unlock budget.",
      points: [
        "Support teams answering the same policy and benefits questions all day.",
        "Each client's employees could only see their own company's policies — and only those meant for their role.",
        "Strict data-privacy rules that ruled out a public cloud assistant.",
        "A working product, not a slide deck, to secure the rollout budget.",
      ],
    },
    goals: [
      "A live assistant inside the HRMS portal in eight weeks.",
      "Role-aware answers: employees, managers and HR admins each see only what they're allowed to.",
      "A design each enterprise customer could rebrand as its own.",
    ],
    approach: [
      { phase: "Conversation design", duration: "2 weeks", body: "Mapped the top HR scenarios and defined role-based answers for employees, managers and HR admins." },
      { phase: "Build", duration: "4 weeks", body: "Built the knowledge base from seeded policies, the role-aware layer and the embedded widget with single sign-on." },
      { phase: "Demo and harden", duration: "2 weeks", body: "Weekly demos with business and IT stakeholders tuned accuracy and the upload-and-approve flow before go-live." },
    ],
    solution: [
      { title: "A role-aware chatbot", body: "Embedded in the HRMS portal, with single sign-on passing each user's identity and role." },
      { title: "Answers from the client's own policies", body: "Cited, with employees, managers and HR admins each seeing only what they're allowed to." },
      { title: "A modular pipeline", body: "Better models swapped in without downtime, and a themeable widget each enterprise customer can rebrand." },
      { title: "On-premise deployment", body: "Running inside the provider's own data centres, with conversation history under its control." },
    ],
    howItRuns: [
      "An employee opens the assistant inside the HRMS portal; single sign-on passes their identity and role.",
      "The question is answered from that company's policies, filtered to what the role may see.",
      "Every answer cites the policy document and section it came from.",
      "Questions the policies don't cover are routed to HR with the conversation attached.",
      "HR admins upload and approve new policy documents; the knowledge base updates without downtime.",
    ],
    snapshot: [
      { label: "Time to live", before: "Quarters", after: "Eight weeks" },
      { label: "Policy questions", before: "Ticket queue", after: "Answered in seconds" },
      { label: "Data location", before: "—", after: "On-premise" },
      { label: "HR workflow efficiency", before: "1×", after: "10×" },
    ],
    capabilities: ["Chatbots", "RAG with citations", "Role-based access", "On-prem deployment", "Embeddable widget"],
    highlights: [
      { metric: "Eight weeks", label: "to a live assistant" },
      { metric: "34", label: "countries served" },
      { metric: "1M+", label: "transactions a month" },
      { metric: "10×", label: "HR workflow efficiency" },
    ],
    result: "Live inside the HRMS portal ahead of schedule in eight weeks, the full roadmap funded on the strength of the demo, and 10× HR workflow efficiency once in production.",
    next: "Spanish and French coverage, and integration with ticketing for end-to-end resolution tracking.",
    related: { page: "solutions-hr-tech", label: "HR Tech on TurfAI" },
    metaDescription: "A global HR services provider went live with a role-aware, on-premise HR assistant in eight weeks on TurfAI, serving employees in 34 countries.",
  }),
  cs({
    slug: "telecom-sms-compliance",
    sector: "Telecom",
    title: "SMS compliance checks at national scale",
    headline: "How a messaging platform checks thousands of SMS templates against India's telecom rules — in minutes.",
    standfirst:
      "Every commercial SMS template in India must pass regulatory checks before it's sent. A leading messaging platform used TurfAI to catch rejections before they happen and give registrars a first-level review they can trust.",
    glance: {
      client: "Enterprise messaging platform and SMS aggregator",
      scale: "Thousands of templates a day, in English, Hindi and Hinglish",
      region: "India",
      deployment: "India-region cloud, Data Shield on",
      timeline: "Proof of concept in 6 weeks",
    },
    context:
      "Businesses in India must register every commercial SMS template before sending it. Rejections cost businesses days, and registrars review every submission by hand — across categories, variables, length rules and opt-out wording, in more than one language.",
    challenge: {
      summary: "Rejections were frequent, reviews were manual, and every verdict had to point to the exact regulation behind it.",
      points: [
        "Templates rejected for the wrong category, misused variables, length or missing opt-out wording.",
        "Registrars reviewing every template by hand, in English, Hindi and Hinglish.",
        "Decisions that had to cite the exact regulation behind them.",
        "Brand names, sender IDs and recipient data had to stay out of model logs.",
      ],
    },
    goals: [
      "Catch rejections before submission, not after.",
      "Give registrars a scored, explained first-level review.",
      "Cite the governing clause on every verdict.",
    ],
    approach: [
      { phase: "Rules mapping", duration: "2 weeks", body: "Turned the regulation into a catalogue of checks, each tied to a clause in the source document." },
      { phase: "Build", duration: "3 weeks", body: "Built the classification workflow, deterministic checks and the 0–100 score, with a registrar console and bulk API." },
      { phase: "Evaluation", duration: "1 week", body: "Ran labelled English and Hindi template sets through the pipeline and reported accuracy against agreed acceptance criteria." },
    ],
    solution: [
      { title: "A classification workflow", body: "Each template's intent and category decided by TurfAI, in English, Hindi or Hinglish." },
      { title: "Deterministic checks", body: "Variables, character limits and required wording, scored 0–100 with a suggested fix." },
      { title: "Data Shield", body: "Brand names, sender IDs and recipient data tokenised before the model sees them." },
      { title: "Registrars decide", body: "Approve, Reject or Return — and their corrections feed back into the next decision." },
    ],
    howItRuns: [
      "A business submits a template through the portal or the bulk API.",
      "Data Shield tokenises brand and sender details.",
      "TurfAI classifies the category; deterministic checks test variables, length and wording.",
      "The template gets a 0–100 score, a recommendation and the clause behind each finding.",
      "A registrar approves, rejects or returns it; corrections improve the next decision.",
    ],
    snapshot: [
      { label: "Review", before: "Manual, one by one", after: "Scored first-level review" },
      { label: "Throughput", before: "A few an hour", after: "131 a minute" },
      { label: "Category accuracy", before: "—", after: "99.8%" },
      { label: "Clause cited", before: "Rarely", after: "Every verdict" },
    ],
    capabilities: ["Classification", "Data Shield", "Human-in-the-loop", "Bulk API", "Signed webhooks"],
    highlights: [
      { metric: "99.8%", label: "category accuracy" },
      { metric: "131/min", label: "templates checked" },
      { metric: "0", label: "compliant templates wrongly failed" },
      { metric: "3", label: "languages: English, Hindi, Hinglish" },
    ],
    result: "99.8% category accuracy across 5,000 templates at 131 templates a minute, with zero compliant templates wrongly failed.",
    next: "Duplicate and similarity checks against the live template corpus, and integration into the production registrar portal.",
    related: { page: "solutions-telecom", label: "Telecom on TurfAI" },
    metaDescription: "A messaging platform checks SMS templates against India's telecom rules at 99.8% category accuracy and 131 templates a minute on TurfAI.",
  }),
  cs({
    slug: "insurance-life-claims",
    sector: "Insurance",
    title: "Life claims, from first notice to settlement",
    headline: "How a life insurer triages claims in minutes and settles them with five AI agents and one officer.",
    standfirst:
      "Death claims arrive as a stack of certificates, reports and bank proofs — and every day of delay hurts a grieving family. A life insurer moved claims, underwriting, renewals and complaints onto TurfAI.",
    glance: {
      client: "Life insurer",
      scale: "Claims, underwriting, renewals, billing, fraud and complaints",
      region: "India",
      deployment: "Private cloud, Data Shield on policyholder data",
      timeline: "Claims live in eight weeks; seven modules in 6 months",
    },
    context:
      "A life insurer's claims team handles some of the most sensitive work in financial services: settling claims for families who have just lost someone. Every claim needs a stack of documents checked, fraud screened and a settlement drafted — all under a regulator's deadlines.",
    challenge: {
      summary: "Claims took days to triage, fraud screening slowed everyone down, and complaint deadlines were tracked in spreadsheets.",
      points: [
        "Claim documents checked by hand, from claim forms and death certificates to police and medical reports.",
        "Fraud screening that slowed every claim, legitimate or not.",
        "Settlement drafts assembled by hand from evidence spread across systems.",
        "Regulatory deadlines on complaints tracked in spreadsheets.",
      ],
    },
    goals: [
      "Triage every claim within the hour.",
      "Flag suspicious claims without slowing honest ones.",
      "Never miss a regulatory deadline.",
    ],
    approach: [
      { phase: "Claims intake", duration: "Eight weeks", body: "Built the four intake agents for document capture, screening, validation and triage, starting from the Insurance Domain Pack." },
      { phase: "Settlement", duration: "Eight weeks", body: "Added the five-agent settlement squad, bank verification and the officer sign-off step." },
      { phase: "Seven modules", duration: "10 weeks", body: "Extended the same rails to underwriting, renewals, billing, fraud and complaints." },
    ],
    solution: [
      { title: "Four intake agents", body: "Capture, screen, validate and triage every claim from documents, email and call transcripts." },
      { title: "A five-agent settlement squad", body: "Gathers evidence, checks coverage, values the claim, drafts the settlement and reviews compliance — then a claims officer signs." },
      { title: "Checks before money moves", body: "Bank verification before payment, fraud-indicator scoring that flags without accusing, and complaints on a regulatory deadline clock." },
      { title: "Data Shield", body: "Policyholder and nominee data protected throughout, with every step in the audit trail." },
    ],
    howItRuns: [
      "A claim arrives with its documents — by upload, email or the agent portal.",
      "Intake agents capture, screen and validate the documents and confirm the policy is in force.",
      "Fraud indicators are scored; clean claims are acknowledged to the nominee straight away.",
      "The settlement squad drafts the offer with its evidence and compliance review.",
      "A claims officer signs, the bank account is verified, and the claim is paid.",
    ],
    snapshot: [
      { label: "Claim triage", before: "~2 days", after: "Under 20 minutes" },
      { label: "Settlement draft", before: "Assembled by hand", after: "Same day, for sign-off" },
      { label: "Fraud screening", before: "Slowed every claim", after: "Flags only the risky ones" },
      { label: "Complaint deadlines", before: "Spreadsheet", after: "Live clock" },
    ],
    capabilities: ["Agent Squads", "AI Agents", "Data Shield", "Human-in-the-loop", "Insurance Domain Pack"],
    highlights: [
      { metric: "<20 min", label: "claim triage, from two days" },
      { metric: "5", label: "agents per settlement" },
      { metric: "7", label: "operations on one platform" },
      { metric: "0", label: "complaint deadlines missed" },
    ],
    result: "Claims triaged in under 20 minutes instead of two days, settlements drafted for sign-off the same day, and no complaint deadline missed.",
    next: "Broker support and renewal outreach squads, built from the same Insurance Domain Pack.",
    related: { page: "solutions-insurance", label: "Insurance on TurfAI" },
    metaDescription: "A life insurer triages claims in under 20 minutes and settles them with a five-agent squad and officer sign-off on TurfAI.",
  }),
  cs({
    slug: "education-admissions",
    sector: "Education",
    title: "Admissions a university can defend",
    headline: "How a premier research institute runs admissions that can be explained — and replayed — years later.",
    standfirst:
      "Admission decisions at a leading institute have to be right, fair and defensible long after the cycle closes. TurfAI runs its rules, reads its documents and records every decision.",
    glance: {
      client: "Premier research institute",
      scale: "Thousands of applicants a cycle across graduate programmes",
      region: "India",
      deployment: "Institute-controlled environment",
      timeline: "Platform demonstrated in one admission cycle",
    },
    context:
      "A leading research institute admits graduate students against detailed rules on eligibility, cut-offs, merit, tie-breaks, reservation and seat allocation. Its decisions affect people's careers, and they can be questioned by auditors — or courts — years later.",
    challenge: {
      summary: "The rules were complex, the documents were messy, and every decision had to be reproducible long after the cycle closed.",
      points: [
        "Eligibility, cut-off, merit, reservation and seat-allocation rules applied across thousands of applicants.",
        "Application documents in every format, checked by hand.",
        "Rules that change between cycles, with no reliable record of which version decided what.",
        "Decisions that auditors and courts may question years later.",
      ],
    },
    goals: [
      "Keep AI out of the decision path entirely.",
      "Make every decision replayable from its record.",
      "Send low-confidence document reads to a person, never reject on them.",
    ],
    approach: [
      { phase: "Policy capture", duration: "4 weeks", body: "Turned admission policy into versioned rule packs reviewed like code, owned by the admissions office." },
      { phase: "Build", duration: "Eight weeks", body: "Built the deterministic engines, document workflows, guardrails and reviewer screens." },
      { phase: "Cycle run", duration: "1 cycle", body: "Ran a full admission cycle end to end, with maker-checker approvals and a sealed record behind every decision." },
    ],
    solution: [
      { title: "Policy as versioned rule packs", body: "Deterministic engines decide; no AI sits in the decision path." },
      { title: "Document workflows", body: "Every field classified and extracted with a confidence score; low-confidence fields go to a person." },
      { title: "Guardrails on every AI action", body: "Registry, schema, identity and policy checks before anything runs." },
      { title: "Maker-checker approvals", body: "Cut-offs approved by officials, with a tamper-evident decision record behind every admission." },
    ],
    howItRuns: [
      "Applications and documents arrive from the admissions system.",
      "TurfAI reads every document; low-confidence fields go to a reviewer.",
      "The rule pack for this cycle runs eligibility, merit and reservation — with no AI involved.",
      "The admissions office proposes cut-offs; a separate official approves them.",
      "Offers are released, and the full decision record is sealed for replay.",
    ],
    snapshot: [
      { label: "Decision record", before: "Scattered", after: "Sealed, per admission" },
      { label: "Replay a past cycle", before: "Not possible", after: "Any time" },
      { label: "Cut-off approval", before: "Days", after: "Hours" },
      { label: "Audit question", before: "Weeks", after: "Minutes" },
    ],
    capabilities: ["Document extraction", "Guardrails", "Maker-checker", "Audit trail", "Versioned rules"],
    highlights: [
      { metric: "100%", label: "of decisions replayable" },
      { metric: "0", label: "AI in the decision path" },
      { metric: "Hours", label: "to approve cut-offs" },
      { metric: "Minutes", label: "to answer an audit question" },
    ],
    result: "Every admission decision replayable from its record, cut-off approvals in hours rather than days, and audit questions answered in minutes.",
    next: "Integration with the student record system, so admitted students flow straight into enrolment.",
    related: { page: "solutions-education", label: "Education on TurfAI" },
    metaDescription: "A premier research institute runs admissions on TurfAI with versioned rule packs, AI-assisted document reading and a replayable record behind every decision.",
  }),
  cs({
    slug: "basecamp",
    sector: "Enterprise · How we run on TurfAI",
    title: "How we run on TurfAI: Basecamp",
    headline: "How an IT services firm fills client openings from its own bench — on TurfAI.",
    standfirst:
      "In a firm of 300+ engineers, the right person for an opening is often already on the payroll — just invisible in spreadsheets and emailed resumes. We built Basecamp, our own talent platform, on TurfAI to find them first.",
    glance: {
      client: "Ariviti — an IT services firm (our own)",
      scale: "300+ engineers across practices",
      region: "India",
      deployment: "Private cloud, Data Shield on every call",
      timeline: "First release in 10 weeks; iterating every sprint",
    },
    context:
      "As an IT services firm, our revenue depends on putting the right engineer on the right client opening, fast. The knowledge of who can do what lived in people's heads, spreadsheets and inboxes — so openings started with an external search by default.",
    challenge: {
      summary: "We couldn't see our own bench, so we hired outside for skills we already had.",
      points: [
        "Skills, availability and interview feedback lived in spreadsheets and inboxes, so nobody could search them.",
        "Every client opening started with an external search, even when a fitting engineer was coming off a project next month.",
        "Profiles were rewritten by hand for every opening.",
        "Matching people to roles had to protect personal data and stay explainable to the engineers being matched.",
      ],
    },
    goals: [
      "Surface internal engineers first for every opening.",
      "Explain every match, so engineers and leads trust it.",
      "Keep personal data out of every model call.",
    ],
    approach: [
      { phase: "Skills profiles", duration: "4 weeks", body: "Built evidence-backed skills profiles with lead approval, read automatically from resumes and project history." },
      { phase: "Matching", duration: "4 weeks", body: "Added JD reading and explained match scoring, with part-time and month-by-month availability." },
      { phase: "Tailored resumes", duration: "2 weeks", body: "One-click resumes shaped to the opening, and learning suggestions drawn from skill gaps." },
    ],
    solution: [
      { title: "Four governed workflows", body: "JD reader, resume reader, profile summary and match scoring — each started by a signed webhook and polled for its result." },
      { title: "Data Shield on every call", body: "People reach the match workflow as pseudonymous codes, never names." },
      { title: "Schema-checked outputs", body: "Every result validated before Basecamp uses it, with graceful fallback if a workflow is unavailable." },
      { title: "Approval and audit", body: "Profile changes and matches approved by a person and logged." },
    ],
    howItRuns: [
      "A client opening arrives; TurfAI reads the job description into structured requirements.",
      "Engineers' profiles, already read from resumes and projects, are matched as pseudonymous codes.",
      "Each match gets a 0–100 score with skills met and missing, plus availability.",
      "A lead reviews the shortlist and picks the people to put forward.",
      "A tailored resume is generated in one click; every step is logged.",
    ],
    snapshot: [
      { label: "First place to look", before: "External search", after: "Our own bench" },
      { label: "Tailored profile", before: "Days", after: "Hours" },
      { label: "Match explained", before: "Gut feel", after: "Score, skills, availability" },
      { label: "Personal data to models", before: "—", after: "Codes only" },
    ],
    capabilities: ["Workflows", "Data Shield", "Schema validation", "Human-in-the-loop", "Signed webhooks"],
    highlights: [
      { metric: "300+", label: "engineers, one searchable bench" },
      { metric: "Hours", label: "to a tailored profile, not days" },
      { metric: "4", label: "governed TurfAI workflows" },
      { metric: "0", label: "names sent to a model" },
    ],
    result: "Internal engineers now surface first for every opening, profiles go out in hours instead of days, and leads spend their time on judgement, not searching. It's the same pattern any enterprise can run on TurfAI.",
    next: "Assessment results from StageOne feeding straight into each engineer's skills profile.",
    related: { page: "platform-governance", label: "Governance & Controls" },
    metaDescription: "How an IT services firm of 300+ engineers fills client openings from its own bench with Basecamp, built on four governed TurfAI workflows.",
  }),
  cs({
    slug: "education-foundation",
    sector: "Non-profit · Education",
    title: "An education foundation reaching students across India",
    headline: "How a non-profit education foundation is taking opportunity to students across India — on TurfAI.",
    standfirst:
      "For two decades, a non-profit foundation has run awards, labs and mentoring for engineering students across 50+ colleges. Its reach was limited by a paper application once a year. TurfAI turns it into a year-round journey any student can join, in their own language.",
    glance: {
      client: "Non-profit education foundation",
      scale: "50+ colleges, hundreds of student teams a year",
      region: "India — rural, semi-urban and urban campuses",
      deployment: "Cloud, Data Shield on every student record",
      timeline: "Public site first; application journey rolling out in phases",
    },
    context:
      "The foundation helps first-generation and rural students become engineers employers want — through awards, project support, labs and mentoring. Its impact was capped by a once-a-year paper application that favoured students who already had access.",
    challenge: {
      summary: "A paper process, a small team and a mission to reach every corner of the country.",
      points: [
        "Applications on paper, once a year, in one format — students outside the big cities were left out.",
        "A small team and volunteer faculty had to review hundreds of student teams fairly.",
        "Students' personal data — many of them young — had to be protected.",
        "Funders wanted to see impact, not just activity.",
      ],
    },
    goals: [
      "Let any student apply, any time, in any format and language.",
      "Review more teams fairly with the same faculty.",
      "Show funders impact in numbers.",
    ],
    approach: [
      { phase: "Reach", duration: "Phase 0", body: "A new public site and audience hubs for students, educators, institutions and partners." },
      { phase: "Apply", duration: "Phase 1", body: "Year-round applications in any format and language, triaged and scored on TurfAI, with faculty deciding." },
      { phase: "Learn, build, prove", duration: "Phases 2–4", body: "Learning, a no-code AI lab and rubric-scored projects, leading to certification and placement." },
    ],
    solution: [
      { title: "Apply in any format, any language", body: "Forms, photos of handwritten pages or a voice note; a TurfAI workflow extracts, scores and routes each application." },
      { title: "Faculty decide", body: "AI triages, and every final decision sits with a named faculty member." },
      { title: "Fair by design", body: "Identifying details hidden from reviewers, and Data Shield protecting every student's personal data." },
      { title: "A no-code AI lab and impact dashboards", body: "Students build their own AI workflows on TurfAI; funders see where their support goes." },
    ],
    howItRuns: [
      "A student applies — by form, photo of a handwritten page or voice note, in their own language.",
      "TurfAI extracts the application and pre-scores it against the rubric.",
      "Identifying details are hidden before faculty review.",
      "A named faculty member makes every decision.",
      "Progress through the programme flows into impact dashboards for funders.",
    ],
    snapshot: [
      { label: "When students apply", before: "Once a year", after: "Year-round" },
      { label: "How they apply", before: "Paper form", after: "Form, photo or voice" },
      { label: "Review", before: "Manual, uneven", after: "Triaged, anonymised" },
      { label: "Impact reporting", before: "Anecdotes", after: "Live dashboards" },
    ],
    capabilities: ["Document & voice intake", "Data Shield", "Human-in-the-loop", "No-code AI lab", "Dashboards"],
    highlights: [
      { metric: "50+", label: "colleges reached" },
      { metric: "Year-round", label: "applications, any language" },
      { metric: "100%", label: "of awards decided by faculty" },
      { metric: "20+", label: "years of the programme" },
    ],
    result: "A year-round programme that reaches students in every corner of the country, reviews more teams with the same faculty, and shows its impact in numbers — non-profit scale, enterprise governance.",
    next: "SkillUp, DataLab and StageOne behind one sign-in, carrying each student from first application to certification.",
    related: { page: "solutions-education", label: "Education on TurfAI" },
    metaDescription: "A non-profit education foundation takes a paper, once-a-year application to a year-round, any-language journey on TurfAI — reaching students across India.",
  }),
];
